import { useQueries, UseQueryResult } from '@tanstack/react-query';
import KpiComparisonCard from './KpiComparisonCard';
import { pricingAnalyticsUrls } from 'config/urls';
import axios from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { getParsedGlobalKpiData } from 'hooks/pricingSimulator/analytics/useFetchGlobalKpisData';
import { useScrollSync } from 'hooks/custom/useScrollSync';
import { ReportScenario } from 'types/pricingSimulator';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';

type ComparisonCardsProps = {
    scenarioIds: (number | null)[];
};

const KpiComparisonCards = ({ scenarioIds }: ComparisonCardsProps) => {
    const {
        selectedReportInfo,
        loggedInUser,
        scenarioComparisonState,
        updateComparisonProperty,
        isUpdate,
        showOldValue,
        selectedDimensionListValues,
        filters,
    } = useContext(PricingAnalyticsDataContext);

    const commonGlobalTilesVariables = useMemo(() => {
        return {
            kpi_ids: selectedReportInfo?.baseKPIs?.selectedKPIs || [],
            user_name: loggedInUser,
            report_id: selectedReportInfo?.reportId,
            filters: filters,
            is_update: false, // since global kpi will be triggered from context against report id, we can put false here by default
        };
    }, [selectedReportInfo?.baseKPIs?.selectedKPIs, filters]);

    // Ensure that scenarioIds always has 5 elements (padding with `null`)
    const paddedScenarioIds = useMemo(() => {
        return [...scenarioIds, ...Array(5 - scenarioIds.length).fill(null)];
    }, [scenarioIds]);

    // Call useQueries for exactly 5 scenarios (some may be null)
    const queriesResponses = useQueries({
        queries: paddedScenarioIds.map((scenario_id) => ({
            queryKey: ['psa-scenario-compare-global-kpi-data', Object.values(commonGlobalTilesVariables), scenario_id],
            queryFn: () =>
                scenario_id !== null
                    ? fetchKPIDataForScenario(scenario_id, commonGlobalTilesVariables)
                    : Promise.resolve(null), // Skip fetching if scenario_id is null
            staleTime: 5000,
            enabled:
                scenario_id !== null &&
                (selectedReportInfo?.baseKPIs?.selectedKPIs?.length
                    ? selectedReportInfo?.baseKPIs?.selectedKPIs?.length > 0
                    : false),
        })),
    }) as UseQueryResult<any>[];

    const parentAttributes = useMemo(() => {
        const result: any[] = [];
        const seenIds = new Set<number>();
        if (selectedReportInfo?.baseKPIs?.selectedKPIs) {
            selectedReportInfo?.baseKPIs?.kpiConfig?.forEach((lense) => {
                lense.attributes.forEach((attribute) => {
                    if (
                        selectedReportInfo?.baseKPIs?.selectedKPIs?.includes(attribute.attributeId) &&
                        !seenIds.has(attribute.attributeId)
                    ) {
                        result.push({
                            id: attribute.attributeId,
                            label: attribute.name,
                        });
                        seenIds.add(attribute.attributeId); // Track the IDs already added
                    }
                });
            });
        }
        result.sort((a, b) => a.id - b.id);
        return result;
    }, [selectedReportInfo?.baseKPIs?.selectedKPIs]);

    // Use the custom hook with the class names of the scrollable elements
    useScrollSync({ elementSelectors: ['comparison-kpi-card'] });

    const onAttributeClick = (kpiId: number) => {
        const config = selectedReportInfo?.baseKPIs?.kpiConfig || [];
        outerLoop: for (let i = 0; i < config?.length; i++) {
            for (let j = 0; j < config[i].attributes?.length; j++) {
                if (config[i].attributes[j].attributeId === kpiId) {
                    const clickedRow = config[i].attributes[j];

                    updateComparisonProperty('selectedKpiItem', {
                        id: clickedRow.attributeId,
                        label: clickedRow.name,
                        post: clickedRow.post || '',
                        pre: clickedRow.pre || '',
                    });

                    updateComparisonProperty('tableState', {
                        ...scenarioComparisonState?.tableState,
                        offset: 0,
                        sortObject: {
                            sortBy: selectedDimensionListValues?.[0] || '',
                            sortingOrder: 'asc',
                            sortType: 'actual',
                        },
                    });
                    break outerLoop;
                }
            }
        }
    };

    // DELETE global kpi tile api call
    const { mutate: deleteScenarioFromReportMutation } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.deleteScenarioFromReportUrl,
        method: 'delete',
    });

    const onDeleteHandler = (scenarioId: number) => {
        if (isUpdate) {
            // only for owner allow delete api call
            const payload = {
                report_id: selectedReportInfo?.reportId,
                scenario_id: scenarioId,
                user_name: loggedInUser,
            };
            deleteScenarioFromReportMutation({ ...payload });

            updateComparisonProperty('selectedScenarios', [
                ...(scenarioComparisonState?.selectedScenarios.filter((s) => s.scenarioId !== scenarioId) || []),
            ]);
        }
    };

    return (
        <div className="flex">
            <div className="mr-6">
                <KpiComparisonCard
                    name={'KPI'}
                    scenarioId={0}
                    attributes={parentAttributes}
                    isDeletable={false}
                    isParent={true}
                    onSelect={onAttributeClick}
                    selectedAttributeId={scenarioComparisonState?.selectedKpiItem?.id}
                />
            </div>

            <div className="flex gap-4 overflow-y-scroll pb-2">
                {paddedScenarioIds.map((scenario_id, index) => {
                    const queryResponse = queriesResponses[index];

                    const {
                        isLoading,
                        isError,
                        data: apiData,
                        isFetching,
                        fetchStatus,
                        error: apiError,
                        isSuccess,
                    } = queryResponse;

                    const apiLoading = isLoading || isFetching || fetchStatus === 'paused';
                    const apiSuccess = !apiLoading && !apiError && isSuccess;
                    const [data, setData] = useState<any[]>([]);

                    useEffect(() => {
                        if (apiSuccess && apiData?.data && !apiData?.data?.error) {
                            const parsedData = getParsedGlobalKpiData(apiData?.data);
                            setData(parsedData);
                        }
                    }, [apiData?.data, apiSuccess, isError]);

                    const scenario = scenarioComparisonState?.selectedScenarios?.find(
                        (s: ReportScenario) => s.scenarioId === scenario_id,
                    );

                    return (
                        <KpiComparisonCard
                            key={index}
                            isPrivate={scenario?.isPrivate}
                            name={scenario?.scenarioName}
                            scenarioId={scenario?.scenarioId || 0}
                            isLoading={apiLoading}
                            isEmpty={scenario_id === null}
                            status={scenario?.status}
                            attributes={data}
                            isDeletable={index !== 0 && isUpdate}
                            isParent={false}
                            showOldValue={showOldValue}
                            onSelect={onAttributeClick}
                            selectedAttributeId={scenarioComparisonState?.selectedKpiItem?.id}
                            onDelete={() => onDeleteHandler(scenario?.scenarioId || 0)}
                        />
                    );
                })}
            </div>
        </div>
    );
};
export default KpiComparisonCards;

// Function to fetch KPIs for a given scenarioId
const fetchKPIDataForScenario = async (scenario_id: any, commonGlobalTilesVariables: any): Promise<any> => {
    try {
        const response = await axios({
            method: 'post',
            url: pricingAnalyticsUrls.getGlobalKPIsDataUrl,
            data: { ...commonGlobalTilesVariables, base_scenario_id: scenario_id },
        });
        return response;
    } catch (error: any) {
        return { data: { error: error?.message } };
    }
};
